import {
  Component,
  computed,
  ContentChildren,
  effect,
  ElementRef, inject,
  OnDestroy, OnInit,
  QueryList,
  signal,
  ViewChild,
} from '@angular/core';
import { TabComponent } from '../tab/tab.component';
import { Subscription } from 'rxjs';
import { TabPanelComponent } from '../tab-panel/tab-panel.component';
import { Keys } from '../../../_core/utils/keys';
import { BreakpointObserver } from '@angular/cdk/layout';

type ScrollDirection = 'left' | 'right';

@Component({
  selector: 'opac-tab-group',
  templateUrl: './tab-group.component.html',
  styleUrls: ['./tab-group.component.scss']
})
export class TabGroupComponent implements OnDestroy, OnInit {
  private readonly breakpointObserver = inject(BreakpointObserver);

  private breakpointSubscription!: Subscription;
  private isFirstTimeActivating = true;
  private isFirstTimeProcessing = true;
  private clickSubscriptions: Subscription[] = [];
  private keydownSubscriptions: Subscription[] = [];
  private resizeObserver: ResizeObserver;

  /*
   * Contains all tab components
   */
  readonly tabs = signal<TabComponent[]>([]);
  /*
   * Contains selected tab component
   */
  readonly activeTab = signal<TabComponent | null>(null);
  /*
   * Determines the index of active tab in tabs array
   */
  readonly activeTabIndex = computed<number>(() => this.tabs().findIndex((tab) => tab.id === this.activeTab()?.id));
  /*
   * Contains all tab panel components
   */
  readonly panels = signal<TabPanelComponent[]>([]);
  /*
   * Contains selected tab panel component based on active tab
   */
  readonly activePanel = computed<TabPanelComponent | null>(() =>
    this.panels().find(panel => panel.name === this.activeTab()?.panel)
  );
  /*
   * Determines the position of tab indicator based on active tab
   */
  readonly indicatorPosition = signal<number>(0);
  /*
   * Determines if tab list is scrollable
   */
  readonly tabListScroll = signal<ScrollDirection[]>([]);
  /*
   * Determines if is in mobile resolution
   */
  readonly isMobile = signal<boolean>(false);

  @ViewChild('tabContainer', { static: true }) tabContainer: ElementRef<HTMLElement>;
  @ViewChild('tabList', { static: true }) tabList: ElementRef<HTMLElement>;
  @ContentChildren(TabComponent) set childTabs(value: QueryList<TabComponent>) {
    this.tabs.set(value.toArray());
  }
  @ContentChildren(TabPanelComponent) set childPanels(value: QueryList<TabPanelComponent>) {
    this.panels.set(value.toArray());
  }

  constructor() {
    // Handle side effects when active tab changes
    effect(() => {
      const activeTab = this.activeTab();
      const activePanel = this.activePanel();

      if (!activeTab || !activePanel) {
        return;
      }

      this.tabs().forEach((tab) => {
        tab.active = false;
      });

      this.panels().forEach((panel) => {
        panel.active = false;
      });

      if (!this.isFirstTimeActivating) {
        // Prevent moving the entire page focus to the tab when it is being activated for the first time
        activeTab.focus();
        // Joins keydown and click events to emit only one event
        activeTab.select.emit();
        // Prevent Chrome bug that sets the focus to the closest element after a `scrollIntoView()` call
        activeTab.tabElement.nativeElement.scrollIntoView({ block: 'center', inline: 'nearest' });
      }

      activeTab.active = true;
      activePanel.active = true;

      this.isFirstTimeActivating = false;
    }, { allowSignalWrites: true });

    // Synchronize tabs and panels when any of them changes
    effect(() => {
      if (this.tabs().length === 0 || this.panels().length === 0) {
        return;
      }

      this.syncTabs(this.tabs(), this.panels());
    });

    effect(() => {
      this.refreshIndicatorPosition();
    }, { allowSignalWrites: true });

    this.resizeObserver = new ResizeObserver(() => {
      this.checkTabListScroll();
    });
  }

  ngOnInit() {
    this.resizeObserver.observe(this.tabList.nativeElement);

    this.tabContainer.nativeElement.addEventListener('scroll', () => {
      this.checkTabListScroll();
    });

    this.breakpointSubscription = this.breakpointObserver.observe('(max-width: 768px)').subscribe(({ matches }) => {
      this.isMobile.set(matches);
    });

    this.checkTabListScroll();
  }

  ngOnDestroy() {
    this.resizeObserver.disconnect();
    this.breakpointSubscription.unsubscribe();
  }

  moveScroll(amount: number) {
    this.tabContainer.nativeElement.scrollBy({ left: amount * this.tabContainer.nativeElement.clientWidth, behavior: 'smooth' });
  }

  refreshIndicatorPosition() {
    if (this.activeTab() === null) {
      return 0;
    }

    const tabEl = this.activeTab().tabElement.nativeElement;
    const indicatorWidth = 40;

    /*
     * +----------------+---------+--------+
     * | Inline padding | Desktop | Mobile |
     * +----------------+---------+--------+
     * | Scroll         |       8 |      0 |
     * | No scroll      |      16 |     16 |
     * +----------------+---------+--------+
     */
    let inlinePadding: number;

    if (this.isMobile()) {
      inlinePadding = this.tabListScroll().length > 0 ? 0 : 16;
    } else {
      inlinePadding = this.tabListScroll().length > 0 ? 8 : 16;
    }

    this.indicatorPosition.set(tabEl.offsetLeft + tabEl.clientWidth / 2 - indicatorWidth / 2 - inlinePadding);
  }

  private handleKeyDown(event: KeyboardEvent) {
    let index: number | undefined = undefined;

    if (event.key === Keys.ArrowRight) {
      index = this.activeTabIndex() + 1;

      if (index > this.tabs().length - 1) {
        index = 0;
      }
    }

    if (event.key === Keys.ArrowLeft) {
      index = this.activeTabIndex() - 1;

      if (this.activeTabIndex() <= 0) {
        index = this.tabs().length - 1;
      }
    }

    if (event.key === Keys.Home) {
      index = 0;
    }

    if (event.key === Keys.End) {
      index = this.tabs().length - 1;
    }

    if (index === undefined) {
      return;
    }

    const tab = this.tabs()[index];
    this.activeTab.set(tab);
    event.preventDefault();
  }

  private syncTabs(tabs: TabComponent[], panels: TabPanelComponent[]): void {
    this.keydownSubscriptions.forEach(subscription => subscription.unsubscribe());
    this.clickSubscriptions.forEach(subscription => subscription.unsubscribe());

    let activeTab: TabComponent = tabs[0];

    tabs.forEach((tab) => {
      if (this.isFirstTimeProcessing) {
        if (tab.active) {
          activeTab = tab;
        }
      }

      const panel = panels.find(panel => panel.name === tab.panel);
      if (panel) {
        panel.tabId = tab.id;
      }

      this.keydownSubscriptions.push(tab.keydown.subscribe((event: KeyboardEvent) => {
        this.handleKeyDown(event);
      }));

      this.clickSubscriptions.push(tab.click.subscribe(() => {
        this.activeTab.set(tab);
      }));
    });

    if (this.activeTab() === null) {
      // Wait a tick to DOM render the tabs and make calculations correctly
      requestAnimationFrame(() => {
        this.activeTab.set(activeTab);
      });
    }

    this.isFirstTimeProcessing = false;
  }

  private checkTabListScroll() {
    const scroll = [];

    if (this.tabContainer.nativeElement.scrollLeft > 0) {
      scroll.push('left');
    }

    if (this.tabContainer.nativeElement.scrollLeft + this.tabContainer.nativeElement.clientWidth < this.tabContainer.nativeElement.scrollWidth) {
      scroll.push('right');
    }

    this.tabListScroll.set(scroll);
  }
}
